<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/Cms/Element/CmsElementSidebarFilter.vue
import type { CmsElementSidebarFilter } from '@shopware-pwa/composables-next';
import type { Schemas } from '@shopware/api-client/api-types';
import type {CustomCategory} from "../../../types/customCategory";
import {computedAsync} from "../../../.nuxt/imports";

defineProps<{
    element: CmsElementSidebarFilter;
}>();

const route = useRoute();
const { getCurrentListing, search } = useCategoryListing();
// TODO create a better ID based on the CmsPage or even better CmsSlot identifier
const productListingCriteriaStore = useProductListingCriteriaStore('category');
const { getSupplier } = useSupplier();
const category = useCategory();
const { getTotal } = useListing();
const expandedDescription = ref(false);
const supplier = await getSupplier(category?.category);
const { t } = useI18n();
const categoryTitle = computed( () => {
    if(supplier) {
        return t('productListing.titleCategoryWithSupplier', {
            supplierName: supplier.translated?.name,
            categoryName: category?.category?.value?.translated?.name
        });
    }
    return category?.category?.value?.translated?.name || '';
});

const { criteria, sortingOptions, currentSorting, appliedFilters, areFiltersModified, filters, total } =
    storeToRefs(productListingCriteriaStore);

const onSortChange = async (sorting: Schemas['ProductListingResult']['sorting']) => {
    productListingCriteriaStore.setSorting(sorting);
};

const onFilterChange = async (filters: Schemas['ProductListingResult']['currentFilters']) => {
    productListingCriteriaStore.setFilters(filters);
};

const onResetFilters = async () => {
    productListingCriteriaStore.resetFilters();
};

const onRemoveFilter = async (filter: Schemas['PropertyGroupOption']['id']) => {
    productListingCriteriaStore.removeFilter(filter);
};

watch(
    () => route.query,
    async () => {
        productListingCriteriaStore.updateCriteria(route.query);
        await search(criteria.value);
        productListingCriteriaStore.setSearchResult(getCurrentListing.value as Schemas['ProductListingResult']);
    },
);
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="relative mb-8 flex flex-col gap-2 md:mb-0">
            <div class="flex place-items-end gap-4">
                <h1 class="font-extrabold">{{ categoryTitle }}</h1>
                <span
                    v-if="getTotal === 0 || getTotal > 1"
                    class="my-1"
                    >{{ $t('listing.description.productCount', { count: getTotal }) }}
                </span>
                <span
                    v-if="getTotal === 1"
                    class="my-1"
                    >{{ $t('listing.description.productCountOne') }}
                </span>
            </div>
            <div
                class="relative overflow-hidden md:max-h-full md:overflow-visible"
                :class="{ 'max-h-20': !expandedDescription }"
            >
                <div
                    class="from-transparent absolute inset-0 z-10 bg-gradient-to-b to-neutral-100 md:hidden"
                    :class="{ hidden: expandedDescription }"
                />

                <!-- eslint-disable vue/no-v-html -->
                <span v-html="category.category.value.description" />
            </div>
            <div
                class="absolute -bottom-8 left-0 z-10 cursor-pointer underline md:hidden"
                @click="expandedDescription = !expandedDescription"
            >
                {{ expandedDescription ? $t('listing.description.showLess') : $t('listing.description.showMore') }}
            </div>
        </div>

        <ProductListingSidebar
            v-if="appliedFilters && (total || areFiltersModified)"
            :filters="filters"
            :selected-filters="appliedFilters"
            :full-width="true"
            :show-reset-button="areFiltersModified"
            :sorting-options="sortingOptions"
            :sorting="currentSorting"
            @sorting-changed="onSortChange"
            @filter-changed="onFilterChange"
            @reset-filters="onResetFilters"
            @remove-filter="onRemoveFilter"
        />
    </div>
</template>
